// Not in legacy: a client program's Partner API clients (docs/PARTNER_API.md § 2) — the businesses that issue this
// program's cards from their own systems. Each has its own client id and secret (the secret shown once, here, when
// it is made or rotated), can be switched off, and has a webhook where QOR sends it events.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { adminApi, fundsApi, type PartnerClient, type PartnerSecret, type SettingValue } from '../api';
import { useStaff } from '../components/RequireStaff';
import { SettingsForm } from '../components/SettingsForm';
import { confirmed } from '../components/ui';

type Shown = { title: string; lines: [string, string][] };

export function PartnerApiPanel({
  programId,
  after,
}: {
  programId: string;
  after: (ok: boolean, message: string) => void;
}) {
  const client = useQueryClient();
  const list = useQuery({
    queryKey: ['partner-clients', programId],
    queryFn: () => adminApi.partnerClients(programId),
  });
  const [shown, setShown] = useState<Shown | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const reload = () => client.invalidateQueries({ queryKey: ['partner-clients', programId] });
  const { user } = useStaff();
  const access = useQuery({
    queryKey: ['partner-access', programId],
    queryFn: async () => (await fundsApi.settings(programId)).filter((r) => r.group === 'partner_api'),
  });

  /** Who may call the API with this partner's credentials: QOR's system admins set it. */
  async function saveAccess(change: { set: Record<string, SettingValue>; clear: string[] }) {
    setBusy(true);
    const r = await fundsApi.changeSettings(programId, change);
    setBusy(false);
    after(r.ok, r.ok ? 'API access saved.' : r.error);
    await client.invalidateQueries({ queryKey: ['partner-access', programId] });
    return r.ok ? {} : r.problems;
  }

  function showSecret(r: PartnerSecret, title: string) {
    setShown({
      title,
      lines: [
        ['Client ID', r.clientId],
        ['Client secret', r.clientSecret],
      ],
    });
  }

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const name = String(new FormData(form).get('PartnerClientName') ?? '').trim();
    if (!name) return;
    setBusy(true);
    const r = await adminApi.createPartnerClient(programId, name);
    setBusy(false);
    if ('error' in r) return after(false, r.error);
    form.reset();
    showSecret(r, `Credentials for ${r.client.name}`);
    after(true, 'Partner API client created.');
    await reload();
  }

  async function rotate(c: PartnerClient) {
    if (!confirmed(`Give ${c.name} a new secret? The current one keeps working for 24 hours.`)) return;
    setBusy(true);
    const r = await adminApi.rotatePartnerClient(c.id);
    setBusy(false);
    if ('error' in r) return after(false, r.error);
    showSecret(r, `New secret for ${c.name}`);
    after(true, 'New secret issued. The previous one stops working in 24 hours.');
    await reload();
  }

  async function toggle(c: PartnerClient) {
    const off = c.status === 'active';
    if (off && !confirmed(`Switch ${c.name} off? Its calls are refused from now on.`)) return;
    setBusy(true);
    const r = await adminApi.setPartnerClientStatus(c.id, off ? 'disabled' : 'active');
    setBusy(false);
    after(
      r.success,
      r.success ? (off ? 'Client switched off.' : 'Client switched on.') : (r.error ?? "Couldn't do that."),
    );
    await reload();
  }

  async function saveWebhook(c: PartnerClient, e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const url = String(new FormData(e.currentTarget).get('WebhookUrl') ?? '').trim();
    setBusy(true);
    const r = await adminApi.setPartnerWebhook(c.id, url || null);
    setBusy(false);
    if ('error' in r) return after(false, r.error);
    if (r.webhookSecret)
      setShown({ title: `Webhook signing secret for ${c.name}`, lines: [['Signing secret', r.webhookSecret]] });
    after(true, url ? 'Webhook saved.' : 'Webhook removed: no events are sent.');
    await reload();
  }

  const clients = list.data ?? [];
  return (
    <div className="qc-panel" style={{ marginTop: 20 }}>
      <div className="qc-panel-title">
        <h3>Partner API</h3>
        <span>Businesses that issue this program&apos;s cards from their own systems, with their own credentials.</span>
      </div>

      {shown && (
        <div className="qc-auth-error" role="alert" style={{ marginBottom: 12 }}>
          <strong>{shown.title}</strong> — copy now: this is the only time it is shown.
          <dl className="qc-detail-dl">
            {shown.lines.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>
                  <code>{value}</code>
                </dd>
              </div>
            ))}
          </dl>
          <button type="button" className="qc-button-ghost" onClick={() => setShown(null)}>
            I have copied it
          </button>
        </div>
      )}

      {clients.length === 0 ? (
        <p className="qc-empty">No Partner API client yet.</p>
      ) : (
        <table className="qc-plain-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Client ID</th>
              <th>Status</th>
              <th>Last call</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <PartnerClientRows
                key={c.id}
                c={c}
                open={open === c.id}
                busy={busy}
                onOpen={() => setOpen(open === c.id ? null : c.id)}
                onRotate={() => void rotate(c)}
                onToggle={() => void toggle(c)}
                onWebhook={(e) => void saveWebhook(c, e)}
              />
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={(e) => void create(e)} className="qc-cardholder-filters" style={{ marginTop: 12 }}>
        <input type="text" name="PartnerClientName" placeholder="Business name" aria-label="Business name" required />
        <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
          Create client
        </button>
      </form>
      <div style={{ marginTop: 20 }}>
        <p style={{ color: 'var(--qor-muted)', fontSize: '.8rem', fontWeight: 700, margin: '0 0 10px' }}>API access</p>
        {access.data && (
          <SettingsForm
            key={access.dataUpdatedAt}
            rows={access.data}
            editable={user.role === 'SystemAdmin'}
            busy={busy}
            onSave={saveAccess}
          />
        )}
      </div>
    </div>
  );
}

function PartnerClientRows(props: {
  c: PartnerClient;
  open: boolean;
  busy: boolean;
  onOpen: () => void;
  onRotate: () => void;
  onToggle: () => void;
  onWebhook: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const { c } = props;
  const deliveries = useQuery({
    queryKey: ['partner-deliveries', c.id],
    queryFn: () => adminApi.partnerDeliveries(c.id),
    enabled: props.open,
  });
  return (
    <>
      <tr>
        <td>{c.name}</td>
        <td>
          <code>{c.clientId}</code>
        </td>
        <td>
          <span className={`qc-badge ${c.status === 'active' ? 'qc-badge-success' : 'qc-badge-muted'}`}>
            {c.status === 'active' ? 'Active' : 'Off'}
          </span>
        </td>
        <td>{c.lastUsedAt ? shortDate(c.lastUsedAt) : 'Never'}</td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button type="button" className="qc-button-ghost" onClick={props.onOpen}>
            {props.open ? 'Close' : 'Webhook'}
          </button>{' '}
          <button type="button" className="qc-button-ghost" disabled={props.busy} onClick={props.onRotate}>
            New secret
          </button>{' '}
          <button type="button" className="qc-button-ghost" disabled={props.busy} onClick={props.onToggle}>
            {c.status === 'active' ? 'Switch off' : 'Switch on'}
          </button>
        </td>
      </tr>
      {props.open && (
        <tr>
          <td colSpan={5}>
            <form onSubmit={props.onWebhook} className="qc-cardholder-filters">
              <input
                type="url"
                name="WebhookUrl"
                placeholder="https://partner.example/qor-events"
                aria-label="Webhook URL"
                defaultValue={c.webhookUrl ?? ''}
                style={{ minWidth: 320 }}
              />
              <button type="submit" className="qc-button-ghost" disabled={props.busy}>
                Save webhook
              </button>
            </form>
            <small>
              {c.webhookUrl
                ? `Events: ${c.webhookEvents.length ? c.webhookEvents.join(', ') : 'all'}.`
                : 'No webhook: no events are sent.'}
            </small>
            {(deliveries.data ?? []).length > 0 && (
              <table className="qc-plain-table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Status</th>
                    <th>Tries</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.data!.map((d) => (
                    <tr key={d.id}>
                      <td>{d.eventType}</td>
                      <td>
                        {d.status}
                        {d.lastError && <small> — {d.lastError}</small>}
                      </td>
                      <td>{d.attempts}</td>
                      <td>{shortDate(d.deliveredAt ?? d.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
